import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import {
  LocationAutocomplete,
  type SelectedPlace,
} from '../components/LocationAutocomplete';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  getBookingDraft,
  patchBookingDraft,
} from '../../lib/booking-draft';
import { listCategories } from '../../lib/booking-api';
import {
  addMyAddress,
  listMyAddresses,
  type CustomerSavedAddress,
} from '../../lib/customer-api';
import { useCustomerAuth } from '../../lib/use-customer-auth';

// One-line address for the saved-address cards.
const composeAddr = (a: CustomerSavedAddress): string =>
  [a.address_line1, a.city, a.postcode].filter(Boolean).join(', ');

export default function BookingLocationPage() {
  const navigate = useNavigate();
  const routerLoc = useLocation();
  const { isAuthenticated } = useCustomerAuth();
  const initialAddress: string =
    (routerLoc.state as { address?: string } | null)?.address ??
    getBookingDraft()?.address?.address_line1 ??
    '';
  const [address, setAddress] = useState(initialAddress);
  // Seed from a previously-saved draft address so a returning customer
  // with coordinates already on file isn't forced to re-pick.
  const [place, setPlace] = useState<SelectedPlace | null>(() => {
    const a = getBookingDraft()?.address;
    if (a?.latitude != null && a?.longitude != null) {
      return {
        description: a.address_line1 ?? '',
        place_id: '',
        latitude: a.latitude,
        longitude: a.longitude,
        postcode: a.postcode ?? undefined,
        city: a.city ?? undefined,
        country_code: a.country_code ?? undefined,
      };
    }
    return null;
  });
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Please enter your address');
  // Resolve the picked category's booking_flow so the Next button
  // can route to the matching wizard (SESSION → /booking,
  // TREATMENT → /select-treatment). Defaults to /booking if no
  // category was set yet (direct nav to /booking-location).
  const [nextPath, setNextPath] = useState<'/booking' | '/select-treatment'>(
    '/booking',
  );
  // Picked-category name from the draft — drives the page heading
  // so "Where will your hair take place?" / "...cleaning..." reads
  // correctly per category.
  const [categoryName, setCategoryName] = useState<string>('');

  // C1 address book — a logged-in customer picks one of their saved
  // addresses instead of typing, or adds a new one (which is saved to
  // their book). Guests just use the autocomplete.
  const [saved, setSaved] = useState<CustomerSavedAddress[]>([]);
  const [selectedSavedId, setSelectedSavedId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const selectSaved = (a: CustomerSavedAddress) => {
    setPlace({
      description: a.address_line1,
      place_id: '',
      latitude: Number(a.latitude),
      longitude: Number(a.longitude),
      postcode: a.postcode ?? undefined,
      city: a.city ?? undefined,
      country_code: a.country_code ?? undefined,
    });
    setAddress(a.address_line1);
    setSelectedSavedId(a.id);
    setError(false);
  };

  useEffect(() => {
    const draft = getBookingDraft();
    const catId = draft?.service_category_id;
    if (!catId) return;
    let cancelled = false;
    listCategories()
      .then((res) => {
        if (cancelled) return;
        const cat = res.data.find((c) => c.id === catId);
        setCategoryName(cat?.name ?? '');
        if (cat?.booking_flow === 'TREATMENT') {
          setNextPath('/select-treatment');
        } else {
          setNextPath('/booking');
        }
      })
      .catch(() => {
        // Network failure — keep the default /booking. Worst case
        // the SESSION wizard refuses to render a TREATMENT category
        // and the user clicks Back; not data-destructive.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the customer's saved addresses. Pre-select the one already on
  // the draft (if any), else their default, so they can just hit Next.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const draftHasAddress = getBookingDraft()?.address?.latitude != null;
    listMyAddresses()
      .then((res) => {
        if (cancelled) return;
        const list = res.info ?? [];
        setSaved(list);
        if (list.length === 0) {
          setAddingNew(true); // nothing saved → straight to the search
        } else if (!draftHasAddress) {
          selectSaved(list.find((a) => a.is_default) ?? list[0]);
        }
      })
      .catch(() => {
        if (!cancelled) setAddingNew(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleNext = () => {
    if (!address.trim()) {
      setErrorMessage('Please enter your address');
      setError(true);
      return;
    }
    // The downstream steps need real coordinates — the travel-radius
    // filter on SelectProfessionalPage and account creation on
    // NewToCherryPage both require lat/lng. A free-typed string that
    // wasn't chosen from the suggestions has none, so require a
    // resolved place here rather than silently proceeding and breaking
    // later.
    if (place?.latitude == null || place?.longitude == null) {
      setErrorMessage(
        'Please pick your address from the suggestions so we can match nearby pros.',
      );
      setError(true);
      return;
    }
    // Persist the address onto the inflight booking draft so the
    // availability call on SelectProfessionalPage can drive its
    // travel-radius filter, and so ContinueBookingPage doesn't
    // have to ask again at checkout. Wipe `last_session_step` here so
    // a stale "resume at step 12" from a previous abandoned booking
    // doesn't override the fresh service-selection screen.
    const existingDraft = getBookingDraft();
    const cleanedMeta = { ...(existingDraft?.meta ?? {}) };
    delete cleanedMeta.last_session_step;
    patchBookingDraft({
      address: {
        address_line1: place.description ?? address,
        address_line2: null,
        city: place.city ?? null,
        postcode: place.postcode ?? null,
        country_code: place.country_code ?? null,
        latitude: place.latitude,
        longitude: place.longitude,
      },
      meta: cleanedMeta,
    });
    // A logged-in customer who typed a NEW address gets it saved to
    // their book for next time (best-effort — the booking itself uses
    // the draft regardless of whether this succeeds).
    if (isAuthenticated && addingNew && !selectedSavedId) {
      addMyAddress({
        address_line1: place.description ?? address,
        city: place.city ?? null,
        postcode: place.postcode ?? null,
        country_code: place.country_code ?? null,
        latitude: place.latitude,
        longitude: place.longitude,
      }).catch(() => {});
    }
    navigate(nextPath);
  };

  const showSavedPicker = isAuthenticated && saved.length > 0 && !addingNew;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 md:px-8 pt-6">
        <Link to="/">
          <SecondaryButton icon={<span className="material-icons text-[18px]">navigate_before</span>}>
            Back
          </SecondaryButton>
        </Link>
      </div>

      <div className="max-w-[1140px] mx-auto px-8 pt-24 pb-20">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[34px] leading-[48px] tracking-[-0.0944px] mb-4">
            {categoryName
              ? `Where will your ${
                  // TREATMENT flow (Hair, Beauty & Wellness) reads better as
                  // "your appointment" than "your hair" / "your beauty &
                  // wellness". SESSION (Cleaning / Childcare) keeps the
                  // category name ("your cleaning").
                  nextPath === '/select-treatment'
                    ? 'appointment'
                    : categoryName.toLowerCase()
                } take place?`
              : 'Where will the service take place?'}
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-sm leading-[21px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
            {showSavedPicker
              ? 'Choose a saved address or add a new one.'
              : 'To easily find your address, enter it in this order: number, street, city, postcode'}
          </p>
        </div>

        {showSavedPicker ? (
          /* Saved-address picker (logged-in customers with a book) */
          <div className="max-w-[480px] mx-auto mb-8">
            <div className="space-y-3">
              {saved.map((a) => {
                const isSel = selectedSavedId === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => selectSaved(a)}
                    className={`w-full text-left rounded-xl border p-4 transition-all ${
                      isSel
                        ? 'border-[#8e2a2f] bg-[#fafaf7] shadow-[0_2px_8px_rgba(142,42,47,0.08)]'
                        : 'border-[#e0e0e0] bg-white hover:border-[#bdbdbd]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px]">
                          {a.label || a.address_line1}
                        </p>
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] truncate">
                          {composeAddr(a)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.is_default ? (
                          <span className="text-[11px] font-bold text-[#8e2a2f] uppercase tracking-wide">
                            Default
                          </span>
                        ) : null}
                        {isSel ? (
                          <span className="material-icons text-[#8e2a2f] text-[20px]">
                            check_circle
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                setAddingNew(true);
                setSelectedSavedId(null);
                setPlace(null);
                setAddress('');
              }}
              className="mt-4 inline-flex items-center gap-1 text-[#8e2a2f] font-bold text-[14px] hover:underline"
            >
              <span className="material-icons text-[18px]">add</span>
              Add a new address
            </button>
          </div>
        ) : (
          /* Address search (guests, or "add new") */
          <div className="max-w-[420px] mx-auto mb-8">
            {isAuthenticated && saved.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setAddingNew(false);
                  selectSaved(saved.find((a) => a.is_default) ?? saved[0]);
                }}
                className="mb-3 inline-flex items-center gap-1 text-[#8e2a2f] font-bold text-[13px] hover:underline"
              >
                <span className="material-icons text-[16px]">navigate_before</span>
                Use a saved address
              </button>
            ) : null}
            <LocationAutocomplete
              label="Address"
              value={address}
              onChange={(value) => {
                setAddress(value);
                setError(false);
                // Editing the text away from the chosen suggestion
                // invalidates the resolved coordinates — drop them so we
                // re-require a pick before continuing.
                setPlace((prev) =>
                  prev && prev.description === value ? prev : null,
                );
              }}
              onPlaceSelected={(p) => {
                setPlace(p);
                setAddress(p.description);
                setSelectedSavedId(null);
                setError(false);
              }}
              error={error}
              errorMessage={errorMessage}
              className="w-full"
              containerHeight="h-[72px]"
              icon="location_on"
            />
          </div>
        )}

        {/* Next Button */}
        <div className="max-w-[420px] mx-auto">
          <PrimaryButton onClick={handleNext} className="w-full">
            Next
          </PrimaryButton>
        </div>
      </div>

      <Footer />
    </div>
  );
}
